
import React from "react";
import { Reagent } from "@/types/reagent.types";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Eye, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "@/utils/dateUtils";
import LoadingSpinner from "@/components/common/LoadingSpinner";

interface RemovedReagentsListProps {
  reagents: Reagent[];
  isLoading: boolean;
  reactivatingId: string | null;
  onReactivate: (reagentId: string) => void;
}

const RemovedReagentsList: React.FC<RemovedReagentsListProps> = ({ 
  reagents, 
  isLoading, 
  reactivatingId, 
  onReactivate 
}) => {
  const navigate = useNavigate();
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (reagents.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground mb-4">
            You don't have any removed or swapped reagents.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Removed Reagents ({reagents.length})</h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reagents.map((reagent) => (
          <Card key={reagent.id} className="overflow-hidden">
            <div className="aspect-video bg-muted relative">
              {(reagent.imageUrl || reagent.image_url) ? (
                <img 
                  src={reagent.imageUrl || reagent.image_url} 
                  alt={reagent.name} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-secondary/20">
                  <p className="text-muted-foreground">No image</p>
                </div>
              )}
              <div className="absolute top-2 right-2">
                <span className="bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
                  Unavailable
                </span>
              </div>
            </div>
            <CardContent className="p-4">
              <h4 className="font-semibold truncate">{reagent.name}</h4>
              <p className="text-sm text-muted-foreground mb-2">
                {reagent.category || (reagent.categories && reagent.categories.name) || "Uncategorized"}
              </p>
              <p className="text-sm mb-4">
                <span className="font-medium">Amount:</span>{" "}
                {reagent.amount ? `${reagent.amount}${reagent.amount_unit || ""}` : "Not specified"}
              </p>
              <p className="text-xs text-muted-foreground mb-4">
                Uploaded on {formatDate(reagent.dateAdded || reagent.created_at || "")}
              </p>
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(`/reagents/${reagent.id}`)}
                >
                  <Eye className="h-4 w-4 mr-1" /> View Details
                </Button>
                <Button 
                  variant="secondary"
                  size="sm"
                  onClick={() => onReactivate(reagent.id)}
                  disabled={reactivatingId === reagent.id}
                >
                  {reactivatingId === reagent.id ? (
                    <div className="animate-spin h-4 w-4 mr-1 border-2 border-current border-t-transparent rounded-full" />
                  ) : (
                    <RefreshCcw className="h-4 w-4 mr-1" />
                  )}
                  Reactivate
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default RemovedReagentsList;
